// Which Supabase project a request targets, and its URL and key. Plain module with
// no `next/headers`, so the Edge middleware can use it as well as the server
// client factories in ./server.tsx.

export type DatabaseEnvironment = "local" | "production" | "demo";

/**
 * Production build made to present the demo (npm run demo:build). It is pinned to
 * the local DEMO database: no cookie, caller or runtime can point it elsewhere.
 * Inlined at build time, so it is also reliable in the browser and Edge.
 */
export const IS_DEMO_BUILD = process.env.NEXT_PUBLIC_DEMO_BUILD === "true";

export const resolveRequestedEnvironment = (
  databaseEnvironment: DatabaseEnvironment | undefined,
  isProductionRuntime: boolean,
  cookieOverride: DatabaseEnvironment | undefined,
): DatabaseEnvironment => {
  if (IS_DEMO_BUILD) {
    return "demo";
  }

  if (databaseEnvironment) {
    return databaseEnvironment;
  }

  if (isProductionRuntime) {
    return "production";
  }

  if (cookieOverride === "production" || cookieOverride === "demo") {
    return cookieOverride;
  }

  return "local";
};

export const resolveSupabaseConfig = (
  env: DatabaseEnvironment,
  isProductionRuntime: boolean,
  useServiceRole: boolean,
) => {
  if (IS_DEMO_BUILD && env !== "demo") {
    throw new Error(
      `The demo build only reaches the demo database (requested: "${env}").`,
    );
  }

  let supabaseUrl = "";
  let supabaseKey = "";

  if (env === "local") {
    supabaseUrl =
      process.env.NEXT_PUBLIC_SUPABASE_URL_LOCAL ||
      process.env.NEXT_PUBLIC_SUPABASE_URL ||
      "";

    supabaseKey = useServiceRole
      ? process.env.SUPABASE_SERVICE_ROLE_KEY_LOCAL ||
        process.env.SUPABASE_SERVICE_ROLE_KEY ||
        ""
      : process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_LOCAL ||
        process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
        "";

    if (!supabaseUrl || !supabaseKey) {
      const keyType = useServiceRole ? "service role" : "anon";
      throw new Error(
        `Local Supabase ${keyType} configuration is missing.\n` +
          "Please add to your env file:\n" +
          "NEXT_PUBLIC_SUPABASE_URL_LOCAL=http://localhost:55001\n" +
          (useServiceRole
            ? "SUPABASE_SERVICE_ROLE_KEY_LOCAL=your-local-service-key\n"
            : "NEXT_PUBLIC_SUPABASE_ANON_KEY_LOCAL=your-local-key\n") +
          "Run 'supabase start' to get local credentials.",
      );
    }

    if (
      !supabaseUrl.includes("localhost") &&
      !supabaseUrl.includes("127.0.0.1")
    ) {
      throw new Error(
        "Local Supabase URL does not point to localhost.\n" +
          `Current URL: ${supabaseUrl}\n` +
          "Please set NEXT_PUBLIC_SUPABASE_URL_LOCAL=http://localhost:55001 in your env file.",
      );
    }
  } else if (env === "demo") {
    supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL_DEMO || "";
    supabaseKey = useServiceRole
      ? process.env.SUPABASE_SERVICE_ROLE_KEY_DEMO || ""
      : process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_DEMO || "";

    if (!supabaseUrl || !supabaseKey) {
      const keyType = useServiceRole ? "service role" : "anon";
      throw new Error(
        `Demo Supabase ${keyType} configuration is missing.\n` +
          "Please add to your env file:\n" +
          "NEXT_PUBLIC_SUPABASE_URL_DEMO=http://localhost:55011\n" +
          (useServiceRole
            ? "SUPABASE_SERVICE_ROLE_KEY_DEMO=your-demo-service-key\n"
            : "NEXT_PUBLIC_SUPABASE_ANON_KEY_DEMO=your-demo-key\n") +
          "Run 'npm run demo:start' to get demo credentials.",
      );
    }

    if (
      !supabaseUrl.includes("localhost") &&
      !supabaseUrl.includes("127.0.0.1")
    ) {
      throw new Error(
        "Demo Supabase URL does not point to localhost.\n" +
          `Current URL: ${supabaseUrl}\n` +
          "Please set NEXT_PUBLIC_SUPABASE_URL_DEMO=http://localhost:55011 in your env file.",
      );
    }
  } else {
    supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL_PROD || "";
    supabaseKey = useServiceRole
      ? process.env.SUPABASE_SERVICE_ROLE_KEY_PROD || ""
      : process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_PROD || "";

    if (!supabaseUrl || !supabaseKey) {
      const whereToSet = isProductionRuntime
        ? "production environment variables"
        : ".env.development";
      const keyType = useServiceRole ? "service role" : "anon";
      const expectedVars = useServiceRole
        ? "NEXT_PUBLIC_SUPABASE_URL_PROD and SUPABASE_SERVICE_ROLE_KEY_PROD"
        : "NEXT_PUBLIC_SUPABASE_URL_PROD and NEXT_PUBLIC_SUPABASE_ANON_KEY_PROD";
      throw new Error(
        `Supabase production ${keyType} configuration missing.\n` +
          `Expected ${expectedVars} in ${whereToSet}.\n`,
      );
    }
  }

  return { supabaseUrl, supabaseKey };
};
